import { IcAltitude20 } from '@assets/icons/20x20';
import { IcArrowTriangle } from '@assets/icons/24x24';
import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectAltitudeUnit } from '@redux/settings/settings.selectors';
import { formatDate } from '@utils/date.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { format } from 'date-fns';
import { PropsWithChildren } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateTimeEnum } from '@/types/dateTime.types';

import InfoValue from '../InfoValue';

export type FlightPlanDetailsProps = {
  maxAltitude: number | null;
  mission: string;
  missionStartTime: string;
  missionEndTime: string;
  information: string[];
  provider: string;
  startDate?: string;
  endDate?: string;
};

export default function FlightPlanDetails({
  startDate,
  endDate,
  maxAltitude,
  mission,
  missionStartTime,
  missionEndTime,
  information,
  provider,
  children,
}: Readonly<PropsWithChildren<FlightPlanDetailsProps>>) {
  const { formatMessage } = useIntl();
  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  const formattedStarDate = startDate ? formatDate(startDate, DateTimeEnum.DATE) : null;
  const formattedEndDate = endDate ? formatDate(endDate, DateTimeEnum.DATE) : null;
  const formattedStartTime = startDate ? format(new Date(startDate), "HH':'mm':'ss") : null;
  const formattedEndTime = endDate ? format(new Date(endDate), "HH':'mm':'ss") : null;

  const isSameDay = formattedStarDate === formattedEndDate;

  const formattedMissionStartDate = formatDate(missionStartTime, DateTimeEnum.DATE);
  const formattedMissionEndDate = formatDate(missionEndTime, DateTimeEnum.DATE);
  const formattedMissionStartTime = format(new Date(missionStartTime), "HH':'mm':'ss");
  const formattedMissionEndTime = format(new Date(missionEndTime), "HH':'mm':'ss");

  const isSameDayMission = formattedMissionStartDate === formattedMissionEndDate;

  return (
    <VStack width="100%" height="100%" gap={0.25}>
      <HStack width="100%" padding={4} backgroundColor="neutral.700" gap={3} alignItems="start">
        {startDate && endDate && (
          <InfoValue
            label={formatMessage({ id: 'flightPlanInfos.tab.details.date' })}
            value={
              <Text color="neutral.300" textStyle="screenStd" whiteSpace="pre-line">
                <FormattedMessage
                  id={isSameDay ? 'flightPlanInfos.tab.details.sameDay' : 'flightPlanInfos.tab.details.multipleDays'}
                  values={{
                    startDate: (
                      <Text as="span" key="1" textStyle="screenStd" color="neutral.white">
                        {formattedStarDate}
                      </Text>
                    ),
                    startTime: (
                      <Text as="span" key="2" textStyle="screenStd" color="neutral.white">
                        {formattedStartTime}
                      </Text>
                    ),
                    endDate: (
                      <Text as="span" key="3" textStyle="screenStd" color="neutral.white">
                        {formattedEndDate}
                      </Text>
                    ),
                    endTime: (
                      <Text as="span" key="4" textStyle="screenStd" color="neutral.white">
                        {formattedEndTime}
                      </Text>
                    ),
                  }}
                />
              </Text>
            }
          />
        )}
        <InfoValue
          label={formatMessage({ id: 'flightPlanInfos.tab.details.maxAltitude' })}
          isEmpty={maxAltitude === null}
          value={
            <HStack gap={1} width="100%" alignItems="start">
              <Icon as={IcAltitude20} boxSize="24px" color="neutral.300" />
              <Text textStyle="screenStd">
                {formatUnit(altitudeUnit, convertDistanceUnitFromMeter(altitudeUnit, maxAltitude))}
              </Text>
            </HStack>
          }
        />
      </HStack>
      <HStack width="100%" padding={4} backgroundColor="neutral.700" gap={3}>
        <InfoValue label={formatMessage({ id: 'flightPlanInfos.tab.details.mission' })} value={mission} />
        <InfoValue label={formatMessage({ id: 'flightPlanInfos.tab.details.missionProvider' })} value={provider} />
      </HStack>
      <Box width="100%" padding={4} backgroundColor="neutral.700">
        <InfoValue
          label={formatMessage({ id: 'flightPlanInfos.tab.details.missionDate' })}
          value={
            <Text color="neutral.300" textStyle="screenStd" whiteSpace="pre-line">
              <FormattedMessage
                id={
                  isSameDayMission ? 'flightPlanInfos.tab.details.sameDay' : 'flightPlanInfos.tab.details.multipleDays'
                }
                values={{
                  startDate: (
                    <Text as="span" key="1" textStyle="screenStd" color="neutral.white">
                      {formattedMissionStartDate}
                    </Text>
                  ),
                  startTime: (
                    <Text as="span" key="2" textStyle="screenStd" color="neutral.white">
                      {formattedMissionStartTime}
                    </Text>
                  ),
                  endDate: (
                    <Text as="span" key="3" textStyle="screenStd" color="neutral.white">
                      {formattedMissionEndDate}
                    </Text>
                  ),
                  endTime: (
                    <Text as="span" key="4" textStyle="screenStd" color="neutral.white">
                      {formattedMissionEndTime}
                    </Text>
                  ),
                }}
              />
            </Text>
          }
        />
      </Box>
      <Box width="100%" padding={4} backgroundColor="neutral.700" flex={1}>
        <InfoValue
          label={formatMessage({ id: 'flightPlanInfos.tab.details.information' })}
          value={
            information.length === 0 ? (
              <Text textStyle="screenStd">
                <FormattedMessage id="flightPlanInfos.tab.details.noInformation" />
              </Text>
            ) : (
              <VStack width="100%" gap={3} alignItems="start">
                {information.map((information, index) => (
                  <HStack gap={1} width="100%" key={`${information}-${index}`} align="start">
                    <Icon as={IcArrowTriangle} boxSize="24px" color="neutral.300" />
                    <Text textStyle="screenStd">{information}</Text>
                  </HStack>
                ))}
              </VStack>
            )
          }
        />
      </Box>
      {children}
    </VStack>
  );
}
